import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Chip,
  Rating,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PropertyDetails from './PropertyDetails';
import { buildImageUrl,API_BASE_URL } from '../config/apiConfig';


const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 250"%3E%3Crect fill="%23f0f0f0" width="400" height="250"/%3E%3Ctext x="50%25" y="50%25" font-size="16" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EProperty Image%3C/text%3E%3C/svg%3E';

const PropertyCard = ({ property, onFavorite }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  if (!property) return null;

  const {
    id,
    title = 'Untitled Property',
    description = 'No description',
    location = 'Location not specified',
    price = 'N/A',
    rating = 4.5,
    beds = 0,
    baths = 0,
    area = 'N/A',
    category = 'other',
    status = 'active',
    photo = null,
    images = [],
    ownerName = 'Not Available',
    ownerPhone = 'Not Available',
    ownerEmail = 'Not Available',
  } = property;

  // Get the best available image URL
  React.useEffect(() => {
    const getImageUrl = () => {
      try {
        console.log('🖼️ Property data:', { photo, images });

        // Try to get primary image from array
        if (Array.isArray(images) && images.length > 0) {
          const primaryImage = images.find((img) => img.is_primary === 1) || images[0];
          if (primaryImage?.image_path) {
            const url = buildImageUrl(primaryImage.image_path, 'properties');
            console.log('📸 Using array image:', url);
            return url;
          }
        }

        // Try single photo field
        if (photo) {
          const url = buildImageUrl(photo, 'properties');
          console.log('📸 Using photo field:', url);
          return url;
        }

        // Fallback
        console.warn('⚠️ No image found, using fallback');
        return FALLBACK_IMAGE;
      } catch (error) {
        console.error('❌ Error processing image:', error);
        return FALLBACK_IMAGE;
      }
    };

    setImageSrc(getImageUrl());
  }, [photo, images]);

  const handleImageError = () => {
    console.error('❌ Image failed to load:', imageSrc);
    setImageSrc(FALLBACK_IMAGE);
  };

  const handleViewDetails = () => {
    console.log('✅ View Details clicked, property:', property);
    setDetailsOpen(true);
  };

  const handleFavorite = () => {
    setIsFavorite(!isFavorite);
    if (onFavorite) {
      onFavorite(id, !isFavorite);
    }
    console.log(`❤️ ${!isFavorite ? 'Added to' : 'Removed from'} favorites:`, id);
  };

  const handleShare = () => {
    setShareOpen(true);
  };

  const handleShareOption = (platform) => {
    const text = `Check out this property: ${title} - ₹${parseFloat(price).toLocaleString('en-IN')}`;
    const propertyUrl = `${API_BASE_URL.replace('/api3', '')}/properties/${id}`;

    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + propertyUrl)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(propertyUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(propertyUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(`${text}\n${propertyUrl}`);
        alert('✅ Link copied to clipboard!');
        setShareOpen(false);
        return;
      default:
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    
    setShareOpen(false);
  };

  const handleContactClick = () => {
    setContactOpen(true);
  };

  const handleCall = () => {
    if (ownerPhone && ownerPhone !== 'Not Available') {
      window.location.href = `tel:${ownerPhone}`;
    }
    setContactOpen(false);
  };

  const handleEmail = () => {
    if (ownerEmail && ownerEmail !== 'Not Available') {
      window.location.href = `mailto:${ownerEmail}`;
    }
    setContactOpen(false);
  };

  const handleWhatsApp = () => {
    if (ownerPhone && ownerPhone !== 'Not Available') {
      const message = `Hi, I'm interested in the property: ${title}`;
      const phone = ownerPhone.replace(/[^0-9]/g, '');
      window.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    }
    setContactOpen(false);
  };

  return (
    <>
      <Card 
        sx={{ 
          boxShadow: 2, 
          borderRadius: 2, 
          overflow: 'hidden', 
          transition: 'transform 0.3s, boxShadow 0.3s', 
          '&:hover': { 
            transform: 'translateY(-8px)',
            boxShadow: 4
          } 
        }}
      >
        {/* Image Section */}
        <Box sx={{ position: 'relative', bgcolor: '#f0f0f0' }}>
          {imageSrc && (
            <CardMedia 
              component="img" 
              height="200" 
              image={imageSrc}
              alt={title}
              onError={handleImageError}
              sx={{ objectFit: 'cover' }} 
            />
          )}

          {/* Status Badge */}
          <Chip 
            label={status.toUpperCase()} 
            size="small" 
            color={status === 'active' ? 'success' : 'warning'}
            variant="filled"
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8,
              fontWeight: 700,
            }}
          />

          {/* Favorite Button */}
          <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
            <IconButton
              size="small"
              onClick={handleFavorite}
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { bgcolor: '#fff' },
              }}
            >
              {isFavorite ? (
                <FavoriteIcon sx={{ color: '#e74c3c' }} />
              ) : (
                <FavoriteBorderIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <CardContent>
          <Box sx={{ bgcolor: '#f0f7ff', p: 1.5, borderRadius: 1.5, borderLeft: '3px solid #667eea' }}>
            {/* Title */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}>
              {title}
            </Typography>

            {/* Description */}
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1.5, 
                minHeight: '40px', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {description}
            </Typography>

            {/* Location & Rating */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}
              >
                📍 {location || 'N/A'}
              </Typography>
              <Rating 
                value={parseFloat(rating) || 4.5} 
                readOnly 
                size="small" 
                precision={0.5}
              />
            </Box>

            {/* Features */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              {beds > 0 && <Chip label={`🛏️ ${beds} Beds`} size="small" />}
              {baths > 0 && <Chip label={`🚿 ${baths} Baths`} size="small" />}
              {area && area !== 'N/A' && <Chip label={`📐 ${area}`} size="small" />}
              <Chip label={`📁 ${category}`} size="small" variant="outlined" />
            </Box>

            {/* Price & ID */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                ₹{parseFloat(price).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {id}
              </Typography>
            </Box>

            {/* Owner Information */}
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#333', display: 'block', mb: 0.5 }}>
                👤 {ownerName}
              </Typography>
              <Tooltip title="Call owner">
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#667eea', 
                    cursor: 'pointer',
                    display: 'block',
                  }}
                  onClick={handleCall}
                >
                  📞 {ownerPhone}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>

        {/* Actions */}
        <CardActions sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          <Button 
            size="small" 
            variant="contained"
            sx={{ bgcolor: '#667eea', flex: 1 }}
            onClick={handleViewDetails}
          >
            विस्तार से देखें
          </Button>
          
          <Tooltip title="Share property">
            <IconButton 
              size="small" 
              onClick={handleShare}
              sx={{ color: '#667eea' }}
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Contact owner">
            <Button 
              size="small" 
              variant="outlined"
              onClick={handleContactClick}
              startIcon={<PhoneIcon />}
            >
              Contact
            </Button>
          </Tooltip>
        </CardActions>
      </Card>

      {/* Property Details Dialog */}
      <PropertyDetails 
        open={detailsOpen}
        property={property}
        onClose={() => setDetailsOpen(false)}
      />

      {/* Share Dialog */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)}>
        <DialogTitle>Share Property</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => handleShareOption('whatsapp')}
              sx={{ color: '#25D366', borderColor: '#25D366' }}
            >
              💬 WhatsApp
            </Button>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => handleShareOption('facebook')}
              sx={{ color: '#1877F2', borderColor: '#1877F2' }}
            >
              f Facebook
            </Button>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => handleShareOption('twitter')}
              sx={{ color: '#1DA1F2', borderColor: '#1DA1F2' }}
            >
              𝕏 Twitter
            </Button>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => handleShareOption('copy')}
              sx={{ color: '#667eea', borderColor: '#667eea' }}
            >
              📋 Copy Link
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onClose={() => setContactOpen(false)}>
        <DialogTitle>Contact {ownerName}</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Button 
              variant="contained" 
              fullWidth
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{ bgcolor: '#667eea' }}
              disabled={!ownerPhone || ownerPhone === 'Not Available'}
            >
              Call: {ownerPhone}
            </Button>
            <Button 
              variant="contained" 
              fullWidth
              startIcon={<EmailIcon />}
              onClick={handleEmail}
              sx={{ bgcolor: '#667eea' }}
              disabled={!ownerEmail || ownerEmail === 'Not Available'}
            >
              Email
            </Button>
            {ownerPhone && ownerPhone !== 'Not Available' && (
              <Button 
                variant="outlined" 
                fullWidth
                onClick={handleWhatsApp}
                sx={{ color: '#25D366', borderColor: '#25D366' }}
              >
                💬 WhatsApp
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PropertyCard;